export interface SearchTextMatchRange {
  start: number;
  end: number;
}

const SEARCH_WORD_CHARACTER_RE = /[\p{L}\p{N}]/u;
const COMBINING_MARK_RE = /[\u0300-\u036f]/g;

export const foldSearchText = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(COMBINING_MARK_RE, '')
    .toLowerCase();

const foldSearchTextWithIndexMap = (value: string) => {
  let foldedValue = '';
  const indexMap: SearchTextMatchRange[] = [];

  for (let index = 0; index < value.length; ) {
    const codePoint = value.codePointAt(index);
    const char = codePoint == null ? '' : String.fromCodePoint(codePoint);
    const nextIndex = index + char.length;
    const foldedChar = foldSearchText(char);
    for (const foldedPart of foldedChar) {
      foldedValue += foldedPart;
      indexMap.push({start: index, end: nextIndex});
    }
    index = nextIndex;
  }

  return {foldedValue, indexMap};
};

export function searchTextTokens(query: unknown): string[] {
  return foldSearchText(query)
    .trim()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

export function uniqueSearchTextTokens(query: unknown): string[] {
  const seen = new Set<string>();
  return searchTextTokens(query).filter(token => {
    if (seen.has(token)) return false;
    seen.add(token);
    return true;
  });
}

const isSearchWordStart = (value: string, index: number) =>
  index === 0 || !SEARCH_WORD_CHARACTER_RE.test(value[index - 1]);

const tokenTextMatchRanges = (normalizedValue: string, token: string) => {
  const ranges: SearchTextMatchRange[] = [];
  let startIndex = 0;

  while (startIndex < normalizedValue.length) {
    const matchIndex = normalizedValue.indexOf(token, startIndex);
    if (matchIndex < 0) break;
    if (isSearchWordStart(normalizedValue, matchIndex)) {
      ranges.push({
        start: matchIndex,
        end: matchIndex + token.length,
      });
    }
    startIndex = matchIndex + Math.max(token.length, 1);
  }

  return ranges;
};

const tokenMatchesText = (normalizedValue: string, token: string) =>
  tokenTextMatchRanges(normalizedValue, token).length > 0;

const mergeSearchTextMatchRanges = (ranges: SearchTextMatchRange[]) => {
  const sortedRanges = [...ranges]
    .filter(range => range.start >= 0 && range.end > range.start)
    .sort((left, right) => left.start - right.start || right.end - left.end);
  const mergedRanges: SearchTextMatchRange[] = [];

  sortedRanges.forEach(range => {
    const previous = mergedRanges[mergedRanges.length - 1];
    if (!previous || range.start > previous.end) {
      mergedRanges.push({...range});
      return;
    }
    previous.end = Math.max(previous.end, range.end);
  });

  return mergedRanges;
};

export function searchTextMatchedTokens(
  text: unknown,
  query: unknown,
): string[] {
  const normalizedValue = foldSearchText(text);
  const tokens = uniqueSearchTextTokens(query);
  if (!tokens.length) return [];
  return tokens.filter(token => tokenMatchesText(normalizedValue, token));
}

export function searchTextMatchTokenCount(
  text: unknown,
  query: unknown,
): number {
  return searchTextMatchedTokens(text, query).length;
}

export function matchesSearchText(text: unknown, query: unknown): boolean {
  const tokens = uniqueSearchTextTokens(query);
  if (!tokens.length) return true;
  return searchTextMatchTokenCount(text, query) > 0;
}

export function searchTextMatchRanges(
  text: unknown,
  query: unknown,
): SearchTextMatchRange[] {
  const value = String(text ?? '');
  const {foldedValue, indexMap} = foldSearchTextWithIndexMap(value);
  const tokens = uniqueSearchTextTokens(query);
  if (!value || !tokens.length) return [];

  const ranges: SearchTextMatchRange[] = [];
  tokens.forEach(token => {
    tokenTextMatchRanges(foldedValue, token).forEach(range => {
      const originalStart = indexMap[range.start]?.start ?? range.start;
      const originalEnd = indexMap[range.end - 1]?.end ?? range.end;
      ranges.push({
        start: originalStart,
        end: originalEnd,
      });
    });
  });

  return mergeSearchTextMatchRanges(ranges);
}
